import React, { useMemo, useState } from 'react';
import { Trophy, Download, Network, LayoutGrid } from 'lucide-react';
import { computeScoreComposite, SCORE_FORMULE } from '../../utils/scenario';

const fmtEur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;

const COLONNES = [
  { id: 'dgs_score', label: 'Score SCD' },
  { id: 'treasury', label: 'Trésorerie' },
  { id: 'retail_ca_cumul', label: 'CA cumulé' },
  { id: 'retail_satisfaction', label: 'Satisfaction' },
];

/**
 * SCD Lot 5 — Classement des équipes (vue Supervision) + export CSV.
 * Score composite par défaut ; tri par colonne au clic.
 */
export default function ClassementSCD({ items }) {
  const [sortBy, setSortBy] = useState('dgs_score');

  const rows = useMemo(() => {
    const playing = (items || []).filter(i => i.snapshot && (i.snapshot.total_days || 0) > 0);
    return playing
      .map(({ license, snapshot }) => {
        const score = snapshot.dgs_score ?? computeScoreComposite({
          treasury: snapshot.treasury || 0,
          caCumul: snapshot.retail_ca_cumul || 0,
          satisfaction: snapshot.retail_satisfaction || 0,
        });
        return {
          key: license.license_key,
          equipe: license.holder_name || '—',
          entreprise: snapshot.company_name || '—',
          secteur: snapshot.retail_secteur || '—',
          jours: snapshot.total_days || 0,
          treasury: snapshot.treasury || 0,
          retail_ca_cumul: snapshot.retail_ca_cumul || 0,
          retail_satisfaction: snapshot.retail_satisfaction ?? 0,
          retail_note_avis: snapshot.retail_note_avis ?? '',
          retail_nb_magasins: snapshot.retail_nb_magasins ?? 1,
          retail_ca_web_cumul: snapshot.retail_ca_web_cumul || 0,
          scenario_id: snapshot.scenario_id || '',
          dgs_score: score,
          franchise: snapshot.retail_franchise || null,
          merch: snapshot.retail_merch || null,
        };
      })
      .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  }, [items, sortBy]);

  const franchiseRows = useMemo(() =>
    rows
      .filter((r) => r.franchise)
      .map((r) => ({
        ...r,
        frRevenus: (r.franchise.totalDroitsEntree || 0) + (r.franchise.totalRedevances || 0),
      }))
      .sort((a, b) => b.frRevenus - a.frRevenus),
  [rows]);

  // 03/07 — Classement merchandising permanent (score planogramme + parcours).
  const merchRows = useMemo(() =>
    rows
      .filter((r) => r.merch && r.merch.configure)
      .map((r) => ({ ...r, merchScore: r.merch.scoreMoyen ?? Math.round(((r.merch.facingScore ?? 0) + (r.merch.parcoursScore ?? 0)) / 2) }))
      .sort((a, b) => b.merchScore - a.merchScore || (b.merch.impactPct || 0) - (a.merch.impactPct || 0)),
  [rows]);

  const exportCsv = () => {
    const header = ['Rang', 'Équipe', 'Entreprise', 'Secteur', 'Jours joués', 'Trésorerie (€)', 'CA cumulé (€)', 'Satisfaction (/100)', 'Note avis (/5)', 'Points de vente', 'CA web cumulé (€)', 'Scénario', 'Score SCD', 'Franchisés actifs', 'Franchisés perdus', 'Revenus franchise (€)', 'Score planogramme', 'Score parcours', 'Impact merchandising CA (%)'];
    const lines = rows.map((r, i) => [
      i + 1, r.equipe, r.entreprise, r.secteur, r.jours, Math.round(r.treasury),
      Math.round(r.retail_ca_cumul), r.retail_satisfaction, r.retail_note_avis,
      r.retail_nb_magasins, Math.round(r.retail_ca_web_cumul), r.scenario_id, r.dgs_score,
      r.franchise ? r.franchise.nbFranchises || 0 : '',
      r.franchise ? r.franchise.perdus || 0 : '',
      r.franchise ? Math.round((r.franchise.totalDroitsEntree || 0) + (r.franchise.totalRedevances || 0)) : '',
      r.merch?.facingScore ?? '',
      r.merch?.parcoursScore ?? '',
      r.merch?.configure ? r.merch.impactPct : '',
    ]);
    const esc = (v) => {
      let s = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = '\ufeff' + [header, ...lines].map(l => l.map(esc).join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dgs-classement-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (rows.length === 0) return null;

  return (
    <div className="bg-ink-800 border border-ink-600 rounded-lg p-4" data-testid="classement-dgs">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-300" />
          <p className="font-bold text-sm text-slate-100">Classement des équipes</p>
          <span className="text-[10px] text-slate-500">{SCORE_FORMULE}</span>
        </div>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 rounded bg-ink-700 hover:bg-ink-600 border border-ink-600 text-xs text-slate-200 px-3 py-1.5 transition-colors"
          data-testid="export-csv-btn"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500 uppercase text-[10px] border-b border-ink-600">
              <th className="py-1.5 pr-2">#</th>
              <th className="py-1.5 pr-2">Équipe</th>
              <th className="py-1.5 pr-2">Entreprise</th>
              {COLONNES.map((c) => (
                <th key={c.id} className="py-1.5 pr-2">
                  <button
                    onClick={() => setSortBy(c.id)}
                    className={`uppercase text-[10px] transition-colors ${sortBy === c.id ? 'text-purple-300 font-bold' : 'hover:text-slate-300'}`}
                    data-testid={`sort-${c.id}`}
                  >
                    {c.label}{sortBy === c.id ? ' ▼' : ''}
                  </button>
                </th>
              ))}
              <th className="py-1.5">PDV</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className="border-b border-ink-700/60" data-testid={`classement-row-${r.key}`}>
                <td className="py-1.5 pr-2 font-mono">{i === 0 ? '🏆' : i + 1}</td>
                <td className="py-1.5 pr-2 font-bold text-slate-200">{r.equipe}</td>
                <td className="py-1.5 pr-2 text-slate-400">{r.entreprise}</td>
                <td className="py-1.5 pr-2 font-mono font-bold text-purple-200">{r.dgs_score}</td>
                <td className={`py-1.5 pr-2 font-mono ${r.treasury < 0 ? 'text-rose-300' : 'text-slate-300'}`}>{fmtEur(r.treasury)}</td>
                <td className="py-1.5 pr-2 font-mono text-slate-300">{fmtEur(r.retail_ca_cumul)}</td>
                <td className="py-1.5 pr-2 font-mono text-slate-300">{r.retail_satisfaction}/100</td>
                <td className="py-1.5 font-mono text-slate-400">{r.retail_nb_magasins}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {franchiseRows.length > 0 && (
        <div className="mt-5 border-t border-ink-600 pt-4" data-testid="classement-franchise">
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-emerald-300" />
            <p className="font-bold text-sm text-slate-100">Classement franchiseurs</p>
            <span className="text-[10px] text-slate-500">Trié par revenus réseau (droits d'entrée + redevances)</span>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 uppercase text-[10px] border-b border-ink-600">
                  <th className="py-1.5 pr-2">#</th>
                  <th className="py-1.5 pr-2">Équipe</th>
                  <th className="py-1.5 pr-2">Conditions</th>
                  <th className="py-1.5 pr-2">Franchisés actifs</th>
                  <th className="py-1.5 pr-2">Perdus</th>
                  <th className="py-1.5 pr-2">Droits d'entrée</th>
                  <th className="py-1.5 pr-2">Redevances</th>
                  <th className="py-1.5">Revenus réseau</th>
                </tr>
              </thead>
              <tbody>
                {franchiseRows.map((r, i) => (
                  <tr key={r.key} className="border-b border-ink-700/60" data-testid={`franchise-row-${r.key}`}>
                    <td className="py-1.5 pr-2 font-mono">{i === 0 ? '🏆' : i + 1}</td>
                    <td className="py-1.5 pr-2 font-bold text-slate-200">{r.equipe}</td>
                    <td className="py-1.5 pr-2 font-mono text-slate-400">{fmtEur(r.franchise.droitEntree)} · {r.franchise.redevancePct}%</td>
                    <td className="py-1.5 pr-2 font-mono text-emerald-300">{r.franchise.nbFranchises || 0}</td>
                    <td className={`py-1.5 pr-2 font-mono ${(r.franchise.perdus || 0) > 0 ? 'text-rose-300' : 'text-slate-500'}`}>{r.franchise.perdus || 0}</td>
                    <td className="py-1.5 pr-2 font-mono text-slate-300">{fmtEur(r.franchise.totalDroitsEntree)}</td>
                    <td className="py-1.5 pr-2 font-mono text-slate-300">{fmtEur(r.franchise.totalRedevances)}</td>
                    <td className="py-1.5 font-mono font-bold text-emerald-200">{fmtEur(r.frRevenus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {merchRows.length > 0 && (
        <div className="mt-5 border-t border-ink-600 pt-4" data-testid="classement-merchandising">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-sky-300" />
            <p className="font-bold text-sm text-slate-100">Classement merchandising — meilleur planogramme</p>
            <span className="text-[10px] text-slate-500">Moyenne des scores planogramme et parcours client, départagée par l'impact CA</span>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 uppercase text-[10px] border-b border-ink-600">
                  <th className="py-1.5 pr-2">#</th>
                  <th className="py-1.5 pr-2">Équipe</th>
                  <th className="py-1.5 pr-2">Score merch.</th>
                  <th className="py-1.5 pr-2">Planogramme</th>
                  <th className="py-1.5 pr-2">Parcours</th>
                  <th className="py-1.5 pr-2">Effets jour</th>
                  <th className="py-1.5">Impact CA</th>
                </tr>
              </thead>
              <tbody>
                {merchRows.map((r, i) => {
                  const m = r.merch;
                  const sc = (v) => (v == null ? '—' : `${v}/100`);
                  const pct = (v, suf = ' %') => (v ? `${v > 0 ? '+' : ''}${v}${suf}` : '0');
                  return (
                    <tr key={r.key} className="border-b border-ink-700/60" data-testid={`merch-row-${r.key}`}>
                      <td className="py-1.5 pr-2 font-mono">{i === 0 ? '🏆' : i + 1}</td>
                      <td className="py-1.5 pr-2 font-bold text-slate-200">{r.equipe}</td>
                      <td className="py-1.5 pr-2 font-mono font-bold text-sky-200">{r.merchScore}/100</td>
                      <td className="py-1.5 pr-2 font-mono text-slate-300">{sc(m.facingScore)}</td>
                      <td className="py-1.5 pr-2 font-mono text-slate-300">{sc(m.parcoursScore)}</td>
                      <td className="py-1.5 pr-2 font-mono text-slate-400">trafic {pct(m.traficPct)} · conv. {pct(m.conversionPct, ' pt')} · panier {pct(m.panierPct)}</td>
                      <td className={`py-1.5 font-mono font-bold ${(m.impactPct || 0) >= 0 ? 'text-emerald-200' : 'text-rose-300'}`}>{pct(m.impactPct)} · {m.impactEurMois > 0 ? '+' : ''}{fmtEur(m.impactEurMois)}/mois</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
